import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import React from 'react';
import { Button } from '@components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@components/ui/select';
import { I18n } from '../../core/i18n';
import { injectTSDI } from '../../core/tsdi';

interface PaginationProps {
    count: number;
    page: number;
    pageSize: number;
    rowsPerPageOptions: number[];
    onPageChange(page: number): void;
    onPageSizeChange(size: number): void;
}

export const Pagination = ({
    count,
    page,
    pageSize,
    rowsPerPageOptions,
    onPageChange,
    onPageSizeChange
}: PaginationProps) => {
    const { __ } = injectTSDI(I18n);
    const lastPage = Math.max(0, Math.ceil(count / pageSize) - 1);
    const from = count === 0 ? 0 : page * pageSize + 1;
    const to = Math.min(count, (page + 1) * pageSize);
    return (
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{__('cp.table.rows.per.page')}</span>
            <Select
                value={String(pageSize)}
                onValueChange={(value) => onPageSizeChange(Number(value))}
            >
                <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {rowsPerPageOptions.map((size) => (
                        <SelectItem key={size} value={String(size)}>
                            {size}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <span>{`${from}-${to} ${__('cp.table.of')} ${count}`}</span>
            <div className="flex items-center gap-1">
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="First page"
                    disabled={page === 0}
                    onClick={() => onPageChange(0)}
                >
                    <ChevronsLeft />
                </Button>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Previous page"
                    disabled={page === 0}
                    onClick={() => onPageChange(page - 1)}
                >
                    <ChevronLeft />
                </Button>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Next page"
                    disabled={page >= lastPage}
                    onClick={() => onPageChange(page + 1)}
                >
                    <ChevronRight />
                </Button>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Last page"
                    disabled={page >= lastPage}
                    onClick={() => onPageChange(lastPage)}
                >
                    <ChevronsRight />
                </Button>
            </div>
        </div>
    );
};
